import "./index.css"
import "./tds-indeterminate"

import Icon from "@utilities/Icon"
import { useId } from "@utilities/useId"
import classNames from "classnames"
import React, { forwardRef, InputHTMLAttributes } from "react"

export type CheckboxSize = "md" | "sm"

interface CheckboxBaseProps {
  /** Optional description text that appears below the checkbox label. */
  description?: string
  /** Whether the checkbox is in an indeterminate state. If true, checked should also be true. */
  indeterminate?: boolean
  /** Whether the checkbox is in an invalid state. If true, include a description to explain why the checkbox is invalid. */
  invalid?: boolean
  /** The size of the checkbox. */
  size?: CheckboxSize
}

interface CheckboxWithLabel extends CheckboxBaseProps {
  "aria-labelledby"?: never
  /**
   * Label content for the checkbox. When using non-string content
   * (e.g. JSX), it must not contain interactive elements. This
   * includes native HTML elements such as `<a>` and `<button>`, as
   * well as Tapestry or Tapestry-React interactive components.
   */
  label: React.ReactNode
}

interface CheckboxWithAriaLabelledBy extends CheckboxBaseProps {
  "aria-labelledby": string
  label?: never
}

export type CheckboxProps = CheckboxWithLabel | CheckboxWithAriaLabelledBy

export type CheckboxElementProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  keyof CheckboxProps | "type"
> &
  CheckboxProps

/**
 * A checkbox component that renders as a checkbox input with label.
 * Supports various sizes, error states, and disabled states.
 *
 * **Required:** You must provide either:
 * - `label` - Label content for the checkbox
 * - `aria-labelledby` - ID of an external element that provides the label
 *
 * When using non-string content for `label` (e.g. JSX), it must not contain
 * interactive elements. This includes native HTML elements such as `<a>` and
 * `<button>`, as well as Tapestry or Tapestry-React interactive components.
 *
 * @component
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxElementProps>(
  (
    {
      className,
      description,
      id,
      indeterminate = false,
      invalid,
      label,
      size = "md",
      ...restProps
    }: CheckboxElementProps,
    ref
  ) => {
    const combinedClassName = classNames(
      "tds-checkbox",
      size && size === "sm" && `tds-checkbox--sm`,
      invalid && "tds-checkbox--invalid",
      className
    )

    const stableId = useId()
    const checkboxId = id || `tds-checkbox-${stableId}`

    const WrapperElement = indeterminate ? "tds-indeterminate" : React.Fragment

    return (
      <div className={combinedClassName}>
        <WrapperElement>
          <input
            {...restProps}
            aria-describedby={
              description ? `${checkboxId}-description` : undefined
            }
            aria-invalid={invalid}
            id={checkboxId}
            ref={ref}
            type="checkbox"
          />
        </WrapperElement>
        {label && <label htmlFor={checkboxId}>{label}</label>}
        {description && (
          <p
            id={`${checkboxId}-description`}
            className="tds-checkbox-description"
          >
            <Icon
              className="tds-checkbox-description-invalid-icon"
              symbol="general#exclamation-triangle"
              aria-hidden
            />
            {description}
          </p>
        )}
      </div>
    )
  }
)

Checkbox.displayName = "Checkbox"
